import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  FlatList,
  Dimensions,
  ViewToken,
} from 'react-native';
import { EVENTS_COLORS } from '@/constants/residents/mobile/eventsColors';
import { eventStrings } from '@/constants/residents/strings';
import { Event, Photo } from '@/types/residents/event';
import { useEventRsvp } from '@/hooks/residents/useEventRsvp';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { AddToCalendarBottomSheet } from './AddToCalendarBottomSheet';
import {
  formatEventDateShort,
  formatEventTimeRange,
  getEventBadgeInfo,
  getEventCapacityText,
  getEventDescription,
} from '@/utils/residents/events/eventHelpers';

const CalendarIcon = RESIDENTS_SVG_COMPONENTS.CALENDAR;
const ClockIcon = RESIDENTS_SVG_COMPONENTS.CLOCK;
const LocationIcon = RESIDENTS_SVG_COMPONENTS.LOCATION;
const GroupIcon = RESIDENTS_SVG_COMPONENTS.GROUP;
const ChevronDown = RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN;
const ChevronUp = RESIDENTS_SVG_COMPONENTS.CHEVRON_UP;
const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

const IMAGE_SIZE = Dimensions.get('window').width - 64; // 20px padding + 12px image padding each side

interface MobileEventCardProps {
  event: Event;
  isPast?: boolean;
}

/**
 * Mobile Event Card Component
 * Matches Figma design: Event / resident / mobile
 */
export const MobileEventCard: React.FC<MobileEventCardProps> = ({ event, isPast = false }) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  
  const { accept, decline, isUpdating } = useEventRsvp(event, {
    acceptSuccessMessage: 'Marked as going',
    declineSuccessMessage: 'Updated',
  });

  // Image carousel handling
  const photos = event.photos || [];
  const hasMultipleImages = photos.length > 1;
  
  const onViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    if (viewableItems.length > 0 && viewableItems[0].index !== null) {
      setActiveImageIndex(viewableItems[0].index);
    }
  }).current;

  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 50,
  }).current;

  // Get badge info using shared helper
  const badgeInfo = getEventBadgeInfo(event, isPast);
  
  // Map badge style to component style
  const getBadgeStyle = () => {
    if (!badgeInfo) return null;
    switch (badgeInfo.style) {
      case 'attending': return 'attending';
      case 'gradient': return 'gradient';
      default: return 'default';
    }
  };

  // Handle opening calendar modal
  const handleOpenCalendar = useCallback(() => {
    setShowCalendarModal(true);
  }, []);

  const handleCloseCalendar = useCallback(() => {
    setShowCalendarModal(false);
  }, []);

  // Render image carousel item
  const renderImageItem = ({ item }: { item: Photo }) => (
    <Image
      source={{ uri: item.photoUrl }}
      style={styles.carouselImage}
      resizeMode="cover"
    />
  );

  // Render carousel dots
  const renderCarouselDots = () => {
    if (!hasMultipleImages) return null;
    
    return (
      <View style={styles.carouselDotsContainer}>
        <View style={styles.carouselDotsBackground}>
          {photos.map((_, index) => (
            <View
              key={index}
              style={[
                styles.carouselDot,
                index === activeImageIndex && styles.carouselDotActive,
              ]}
            />
          ))}
        </View>
      </View>
    );
  };

  const rsvpLabel = badgeInfo;
  const badgeStyle = getBadgeStyle();
  const capacityText = getEventCapacityText(event);
  const description = getEventDescription(event);
  const shouldTruncate = description.length > 100;
  const displayDescription = showFullDescription || !shouldTruncate 
    ? description 
    : description.substring(0, 100) + '...';

  return (
    <View style={styles.container}>
      {isUpdating && (
        <View style={styles.loadingOverlay}>
          <LoadingSpinner size="small" showMessage={false} variant="resident" />
        </View>
      )}
      
      {/* Image Section */}
      <View style={styles.imageContainer}>
        {photos.length > 0 ? (
          <View style={styles.imageWrapper}>
            <FlatList
              data={photos}
              renderItem={renderImageItem}
              keyExtractor={(item) => item.id.toString()}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onViewableItemsChanged={onViewableItemsChanged}
              viewabilityConfig={viewabilityConfig}
              style={styles.carousel}
            />
            {renderCarouselDots()}
          </View>
        ) : (
          <View style={[styles.imageWrapper, styles.placeholderImage]}>
            <Text style={styles.placeholderText}>No Image</Text>
          </View>
        )}
      </View>

      {/* Info Section */}
      <View style={styles.infoContainer}>
        {/* Summary Section */}
        <View style={styles.summaryContainer}>
          {/* Header Row: Label + Calendar */}
          <View style={styles.headerRow}>
            {rsvpLabel && badgeStyle && (
              <View style={[
                styles.label,
                badgeStyle === 'attending' && styles.labelAttending,
                badgeStyle === 'gradient' && styles.labelGradient,
                badgeStyle === 'default' && styles.labelDefault,
              ]}>
                {rsvpLabel.showCheckmark && CheckIcon && (
                  <CheckIcon width={12} height={12} color={EVENTS_COLORS.attendingBadgeText} />
                )}
                <Text style={[
                  styles.labelText,
                  badgeStyle === 'attending' && styles.labelTextAttending,
                ]}>
                  {rsvpLabel.text}
                </Text>
              </View>
            )}
            {!isPast && (
              <TouchableOpacity onPress={handleOpenCalendar} style={styles.calendarButton}>
                <CalendarIcon width={20} height={20} color={EVENTS_COLORS.calendarIconActive} />
              </TouchableOpacity>
            )}
          </View>

          {/* Event Title */}
          <Text style={styles.title}>{event.name || 'Event'}</Text>

          {/* Event Info Grid */}
          <View style={styles.infoGrid}>
            {/* Left Column */}
            <View style={styles.infoColumn}>
              <View style={styles.infoItem}>
                <CalendarIcon width={20} height={20} color={EVENTS_COLORS.iconGray} />
                <Text style={styles.infoText}>{formatEventDateShort(event.startTime)}</Text>
              </View>
              <View style={styles.infoItem}>
                <ClockIcon width={20} height={20} color={EVENTS_COLORS.iconGray} />
                <Text style={styles.infoText}>{formatEventTimeRange(event.startTime, event.endTime)}</Text>
              </View>
            </View>

            {/* Right Column */}
            <View style={styles.infoColumn}>
              {event.location && (
                <View style={styles.infoItem}>
                  <LocationIcon width={20} height={20} color={EVENTS_COLORS.iconGray} />
                  <Text style={styles.infoText}>{event.location}</Text>
                </View>
              )}
              {capacityText && (
                <View style={styles.infoItem}>
                  <GroupIcon width={20} height={20} color={EVENTS_COLORS.iconGray} />
                  <Text style={styles.infoText}>{capacityText}</Text>
                </View>
              )}
            </View>
          </View>

          {/* RSVP Button - Single button based on state (Figma design) */}
          {!isPast && (
            <View style={styles.buttonsContainer}>
              {event.rsvp ? (
                // User is attending - show "I can't attend" outline button
                <TouchableOpacity
                  style={[styles.button, styles.buttonSecondary]}
                  onPress={decline}
                  disabled={isUpdating}
                >
                  <Text style={[styles.buttonText, styles.buttonSecondaryText]}>
                    {eventStrings.buttons.cantAttend}
                  </Text>
                </TouchableOpacity>
              ) : (
                // User not attending - show "I will attend" solid button
                <TouchableOpacity
                  style={[styles.button, styles.buttonPrimary]}
                  onPress={accept}
                  disabled={isUpdating}
                >
                  <Text style={[styles.buttonText, styles.buttonPrimaryText]}>
                    {eventStrings.buttons.willAttend}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>

        {/* Description Section */}
        {description && (
          <View style={styles.descriptionContainer}>
            <Text style={styles.descriptionText}>{displayDescription}</Text>
            {shouldTruncate && (
              <TouchableOpacity
                style={styles.showMoreContainer}
                onPress={() => setShowFullDescription(!showFullDescription)}
              >
                <Text style={styles.showMoreText}>
                  {showFullDescription ? eventStrings.showLess : eventStrings.showMore}
                </Text>
                {showFullDescription ? (
                  <ChevronUp width={20} height={20} color={EVENTS_COLORS.linkBlue} />
                ) : (
                  <ChevronDown width={20} height={20} color={EVENTS_COLORS.linkBlue} />
                )}
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      {/* Add to Calendar Modal */}
      <AddToCalendarBottomSheet
        visible={showCalendarModal}
        onClose={handleCloseCalendar}
        event={event}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: EVENTS_COLORS.cardBackground,
    borderRadius: 16,
    overflow: 'hidden',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Image Section
  imageContainer: {
    padding: 12,
    alignItems: 'center',
  },
  imageWrapper: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: EVENTS_COLORS.imageBackground,
  },
  carousel: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
  },
  carouselImage: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
  },
  placeholderImage: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderText: {
    color: EVENTS_COLORS.textMuted,
    fontSize: 14,
  },
  
  // Carousel Dots
  carouselDotsContainer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  carouselDotsBackground: {
    flexDirection: 'row',
    backgroundColor: EVENTS_COLORS.carouselBackground,
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 6,
  },
  carouselDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: EVENTS_COLORS.carouselDotInactive,
  },
  carouselDotActive: {
    backgroundColor: EVENTS_COLORS.carouselDotActive,
  },
  
  // Info Section
  infoContainer: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 20,
    gap: 20,
  },
  
  // Summary Section
  summaryContainer: {
    gap: 20,
    borderBottomWidth: 1,
    borderBottomColor: EVENTS_COLORS.borderColor,
    paddingBottom: 20,
  },
  
  // Header Row
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    paddingHorizontal: 10,
    paddingVertical: 2,
    borderRadius: 60,
    height: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  labelDefault: {
    backgroundColor: EVENTS_COLORS.labelBackground,
  },
  labelGradient: {
    // Gradient approximation - in production would use LinearGradient
    backgroundColor: '#B53369', // Midpoint of gradient
  },
  labelAttending: {
    backgroundColor: EVENTS_COLORS.attendingBadgeBackground,
    flexDirection: 'row',
    gap: 4,
    paddingLeft: 4,
    paddingRight: 8,
  },
  labelText: {
    color: EVENTS_COLORS.textPrimary,
    fontSize: 12,
    fontWeight: '500',
  },
  labelTextAttending: {
    color: EVENTS_COLORS.attendingBadgeText,
  },
  calendarButton: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 'auto', // Always push to right
  },
  
  // Title
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: EVENTS_COLORS.textPrimary,
    lineHeight: 27,
  },
  
  // Info Grid
  infoGrid: {
    flexDirection: 'row',
    gap: 20,
  },
  infoColumn: {
    gap: 12,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoText: {
    fontSize: 14,
    color: EVENTS_COLORS.textPrimary,
    fontWeight: '400',
  },
  
  // Buttons
  buttonsContainer: {
    flexDirection: 'row',
  },
  button: {
    flex: 1,
    height: 34,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  buttonPrimary: {
    backgroundColor: EVENTS_COLORS.buttonPink,
  },
  buttonSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: EVENTS_COLORS.buttonPink,
  },
  buttonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  buttonPrimaryText: {
    color: EVENTS_COLORS.buttonPinkText,
  },
  buttonSecondaryText: {
    color: EVENTS_COLORS.buttonPink,
  },
  
  // Description
  descriptionContainer: {
    gap: 8,
  },
  descriptionText: {
    fontSize: 12,
    fontWeight: '300',
    color: EVENTS_COLORS.textMuted,
    lineHeight: 18,
  },
  showMoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  showMoreText: {
    fontSize: 12,
    fontWeight: '500',
    color: EVENTS_COLORS.linkBlue,
  },
});

export default MobileEventCard;
